import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/* ── Dialog Context ──────────────────────────────────────────────────── */

interface DialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | null>(null);

function useDialogContext() {
  const ctx = React.useContext(DialogContext);
  if (!ctx) throw new Error("Dialog components must be used within <Dialog>");
  return ctx;
}

/* ── Dialog Root ─────────────────────────────────────────────────────── */

export interface DialogProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Dialog root - manages open/close state. */
function Dialog({ children, open: controlledOpen, defaultOpen = false, onOpenChange }: DialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlledOpen, onOpenChange]
  );

  return (
    <DialogContext.Provider value={{ open, setOpen }}>
      {children}
    </DialogContext.Provider>
  );
}

/* ── DialogTrigger ───────────────────────────────────────────────────── */

export interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ className, onClick, ...props }, ref) => {
    const { setOpen } = useDialogContext();

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          setOpen(true);
          onClick?.(e);
        }}
        className={cn(className)}
        {...props}
      />
    );
  }
);
DialogTrigger.displayName = "DialogTrigger";

/* ── DialogClose ─────────────────────────────────────────────────────── */

const DialogClose = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => {
    const { setOpen } = useDialogContext();

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          setOpen(false);
          onClick?.(e);
        }}
        className={cn(className)}
        {...props}
      />
    );
  }
);
DialogClose.displayName = "DialogClose";

/* ── DialogContent ───────────────────────────────────────────────────── */

export interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  hideBackdrop?: boolean;
  hideCloseButton?: boolean;
  side?: "center" | "right";
  /** Use above-panels when opening a dialog over SlidePanel (z 250+). */
  stackLayer?: "default" | "above-panels";
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, children, hideBackdrop = false, hideCloseButton = false, side = "center", stackLayer = "default", ...props }, ref) => {
    const { open, setOpen } = useDialogContext();
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
      setMounted(true);
      return () => setMounted(false);
    }, []);

    // Close on Escape
    React.useEffect(() => {
      if (!open) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [open, setOpen]);

    // Prevent body scroll
    React.useEffect(() => {
      if (open) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const previousPaddingRight = document.body.style.paddingRight;
        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
          document.body.style.overflow = previousOverflow;
          document.body.style.paddingRight = previousPaddingRight;
        };
      } else {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      };
    }, [open]);

    if (!open || !mounted) return null;

    const isRightDrawer = side === "right";
    const stackZ = stackLayer === "above-panels" ? "z-[300]" : "z-50";
    const contentZ = stackLayer === "above-panels" ? "z-[301]" : "z-60";

    return createPortal(
      <div className={cn("fixed inset-0 overflow-y-auto", stackZ)}>
        {/* Backdrop */}
        {!hideBackdrop && (
          <div
            className="fixed inset-0 bg-[#002a31]/30 backdrop-blur-[2px] animate-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
        )}
        {/* Content */}
        <div
          className={cn(
            "relative flex min-h-full",
            contentZ,
            isRightDrawer
              ? "items-stretch justify-end p-2 sm:p-3 pointer-events-none"
              : "items-end justify-center p-3 sm:items-center sm:p-6",
          )}
        >
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            className={cn(
              isRightDrawer
                ? "relative w-full max-w-140 h-[calc(100vh-1rem)] max-h-[calc(100vh-1rem)] overflow-hidden pointer-events-auto"
                : "relative my-auto w-full max-w-lg overflow-hidden max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-3rem)] rounded-[24px] bg-card text-card-foreground animate-scale-in p-6 shadow-[0_24px_64px_-20px_rgba(0,42,49,0.45)]",
              className
            )}
            {...props}
          >
            {children}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={cn(
                  "absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full",
                  "bg-[#f8f3eb] text-[#003a43] dark:bg-muted dark:text-foreground",
                  "hover:bg-[#f1ece2] transition-colors",
                  "sm:right-4 sm:top-4",
                )}
                aria-label="Close"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }
);
DialogContent.displayName = "DialogContent";

/* ── DialogHeader ────────────────────────────────────────────────────── */

const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 text-left", className)}
      {...props}
    />
  )
);
DialogHeader.displayName = "DialogHeader";

/* ── DialogFooter ────────────────────────────────────────────────────── */

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4",
        className
      )}
      {...props}
    />
  )
);
DialogFooter.displayName = "DialogFooter";

/* ── DialogTitle ─────────────────────────────────────────────────────── */

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn("text-base font-bold leading-snug tracking-tight text-foreground", className)}
      {...props}
    />
  )
);
DialogTitle.displayName = "DialogTitle";

/* ── DialogDescription ───────────────────────────────────────────────── */

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm leading-relaxed text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
